import type { Role } from '@salonomy/types';

/**
 * Client-side mirror of apps/api/src/core/rbac.ts. Kept byte-for-byte in step with the server so
 * the UI surfaces role-escalation guards as DISABLED options with an explanation — a server 403 is
 * never the first feedback a manager gets. If the API's RANK/assertCanAssignRole changes, change
 * this too (they are the same rule, expressed twice for UX).
 */
const RANK: Record<Role, number> = {
  receptionist: 1,
  stylist: 2,
  chair_renter: 2,
  manager: 3,
  owner: 4,
};

/**
 * Can `actor` assign `target`? Mirror of assertCanAssignRole: the owner may assign anything; anyone
 * else may only assign a role ranked strictly below their own (no self-promotion, no minting peers).
 */
export function canAssignRole(actor: Role, target: Role): boolean {
  if (actor === 'owner') return true;
  return RANK[target] < RANK[actor];
}

/** Why an option is disabled, for the inline explanation next to the Select. */
export function assignRoleReason(actor: Role, target: Role): string | null {
  if (canAssignRole(actor, target)) return null;
  return `You cannot assign “${ROLE_LABEL[target]}” — it is at or above your own rank.`;
}

/**
 * Only an owner may edit an owner's record (mirror of the PATCH /staff guard). Used to lock the
 * whole form when a manager opens an owner.
 */
export function canEditStaffRole(actorRole: Role, targetRole: Role): boolean {
  if (targetRole === 'owner' && actorRole !== 'owner') return false;
  return true;
}

export const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner',
  manager: 'Manager',
  stylist: 'Stylist',
  chair_renter: 'Chair renter',
  receptionist: 'Reception',
};

export const ALL_ROLES: Role[] = ['owner', 'manager', 'stylist', 'chair_renter', 'receptionist'];
